import { z } from 'zod';
import { UuidSchema, NonEmptyStringSchema } from './common.schema';

// Category schemas
export const CategorySchema = z.object({
  id: UuidSchema,
  group_id: UuidSchema,
  name: NonEmptyStringSchema,
  emoji: NonEmptyStringSchema,
  pinned: z.boolean().optional(),
});

export const CategorySplitSchema = z.object({
  id: UuidSchema,
  category_id: UuidSchema,
  user_id: UuidSchema,
  weight: z.number().min(0),
});

export const CategorySplitWithNameSchema = z.object({
  user_id: UuidSchema,
  weight: z.number().min(0),
  name: NonEmptyStringSchema,
});

export const CategoryWithSplitsSchema = CategorySchema.extend({
  splits: z.array(CategorySplitWithNameSchema).nullable().optional(),
  total_spent: z.union([z.number(), z.string(), z.null()]).optional(),
  expense_count: z.union([z.number(), z.string(), z.null()]).optional(),
});

export const CategorySummaryDistributionSchema = z.record(
  z.string(),
  z.object({
    amount: z.number(),
    fraction: z.number().min(0).max(1),
  })
);

export const CategorySummarySchema = z.object({
  category: CategorySchema,
  distribution: CategorySummaryDistributionSchema,
});

// Request schemas
export const CreateCategoryRequestSchema = z.object({
  group: UuidSchema,
  name: NonEmptyStringSchema,
  emoji: NonEmptyStringSchema,
});

export const UpdateCategoryRequestSchema = z.object({
  name: NonEmptyStringSchema,
  emoji: NonEmptyStringSchema,
});

export const UpdateCategorySplitsRequestSchema = z.object({
  splits: z.array(
    z.object({
      user_id: UuidSchema,
      weight: z.number().min(0),
    })
  ),
});

// Response schemas
export const CategoriesInGroupResponseSchema = z.object({
  categories: z.array(CategoryWithSplitsSchema),
});

export const CategorySplitsResponseSchema = z.object({
  splits: z.array(CategorySplitWithNameSchema),
});

export const CategoryResponseSchema = z.object({
  category: CategorySchema,
});

export const CategorySummaryResponseSchema = CategorySummarySchema;

// Derived types
export type Category = z.infer<typeof CategorySchema>;
export type CategorySplit = z.infer<typeof CategorySplitSchema>;
export type CategorySplitWithName = z.infer<typeof CategorySplitWithNameSchema>;
export type CategoryWithSplits = z.infer<typeof CategoryWithSplitsSchema>;
export type CategorySummaryDistribution = z.infer<typeof CategorySummaryDistributionSchema>;
export type CategorySummary = z.infer<typeof CategorySummarySchema>;
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;
export type UpdateCategoryRequest = z.infer<typeof UpdateCategoryRequestSchema>;
export type UpdateCategorySplitsRequest = z.infer<typeof UpdateCategorySplitsRequestSchema>;
export type CategoriesInGroupResponse = z.infer<typeof CategoriesInGroupResponseSchema>;
export type CategorySplitsResponse = z.infer<typeof CategorySplitsResponseSchema>;
export type CategoryResponse = z.infer<typeof CategoryResponseSchema>;